import { defineStore } from "pinia";
import { computed, ref, shallowRef } from "vue";

import type { Board } from "@/model/board";
import type { Card } from "@/model/card";
import { useGlobalI18n } from "@/translations/i18n";
import { asReadonly } from "@/utils/vue";

import { useBoardStore } from "../board";
import { useSearchMenuStore } from "./searchMenu";
import type { GraphSignalId, HelpParagraph, Signal, SignalId } from "./signalDefinitions";
import { boardSignals, signalGroups } from "./signalDefinitions";

export interface SignalItem {
  id: SignalId;
  label: string;
  help?: HelpParagraph[];
  ariaDescription?: string; // help as one text, for screen readers
  count?: number; // graph signals: undefined until a ready result for this board exists
}

export interface SignalItemGroup {
  subheader?: string;
  values: SignalItem[];
}

export interface GraphResults {
  boardId: string;
  // null: fetched but not ready (unavailable, pending retries exhausted, error)
  ids: Record<GraphSignalId, ReadonlySet<string>> | null;
}

const { t } = useGlobalI18n();

/** Signals of the current board: counts, selection and graph results (fetched by signalSync). */
export const useSignalStore = defineStore("signals", () => {
  const graphResults = shallowRef<GraphResults | null>(null);
  const loadingBoardId = ref<string | null>(null);

  const loading = computed(() => loadingBoardId.value === useBoardStore().board?.id);

  const graphReady = computed(
    () => graphResults.value?.boardId === useBoardStore().board?.id && !!graphResults.value?.ids,
  );

  const signals = computed(() => {
    const board = useBoardStore().board;
    return board ? boardSignals(board) : [];
  });

  const items = computed((): SignalItem[] => {
    const board = useBoardStore().board;
    if (!board) return [];
    const cards = Object.values(board.cards).map((boardCard) => boardCard.data);
    return signals.value.map((signal) => {
      const help = signal.help?.filter((paragraph) => paragraph.when?.() ?? true);
      return {
        id: signal.id,
        label: t(signal.label),
        help,
        ariaDescription: help && helpDescription(help),
        count: count(signal, cards, board),
      };
    });
  });

  const groups = computed((): SignalItemGroup[] =>
    signalGroups
      .map((group) => ({
        subheader: group.subheader && t(group.subheader),
        values: items.value.filter((item) => group.ids.includes(item.id)),
      }))
      .filter((group) => group.values.length > 0),
  );

  const selectedItems = computed(() =>
    items.value.filter((item) => useSearchMenuStore().selectedSignals.has(item.id)),
  );

  const graphSignalSelected = computed(() =>
    useSearchMenuStore().activeSignals.some((signal) => signal.source === "graph"),
  );

  /** Marks graph signals as loading for the board, or not loading (null). */
  function setLoading(boardId: string | null) {
    loadingBoardId.value = boardId;
  }

  /** Stores the graph signal results and ends loading. */
  function setResults(results: GraphResults) {
    graphResults.value = results;
    loadingBoardId.value = null;
  }

  /** Whether the card matches the signal: client rules, or graph results for graph signals. */
  function matches(signal: Signal, card: Card, board: Board): boolean {
    if (signal.source === "client") return signal.matches(card, board);
    const ids = matchingIds(signal.id, board);
    // graph ids are origin ids, so a mirror matches via its groupId
    return !!ids && (ids.has(card.id) || (!!card.groupId && ids.has(card.groupId)));
  }

  /** The number shown for the signal; undefined for a graph signal without results. */
  function count(signal: Signal, cards: Card[], board: Board): number | undefined {
    if (signal.source === "client" && signal.count) return signal.count();
    if (signal.source === "graph" && !matchingIds(signal.id, board)) return undefined;
    return cards.filter((card) => matches(signal, card, board)).length;
  }

  /** Ids of the stickies matching the graph signal on the board; undefined until a ready result for that board exists. */
  function matchingIds(id: GraphSignalId, board: Board): ReadonlySet<string> | undefined {
    const results = graphResults.value;
    return results?.boardId === board.id ? results.ids?.[id] : undefined;
  }

  return {
    graphResults: asReadonly(graphResults),
    signals,
    groups,
    selectedItems,
    graphSignalSelected,
    loading,
    graphReady,
    matches,
    setLoading,
    setResults,
  };
});

/** The help paragraphs as one translated text. */
function helpDescription(help: HelpParagraph[]): string {
  return help.map(({ text, em }) => (em ? t(text, { em: t(em) }) : t(text))).join(" ");
}
